import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

export const EditorContext = React.createContext(null);

export const EditorModeContext = React.createContext();

export const EditorModeContextProvider = ({ children }) => {
  const location = useLocation();
  const history = useHistory();
  const initialQuery = new URLSearchParams(location.search);
  const initialTagging = location.state?.taggingMode || initialQuery.get('taggingMode') === 'true';
  const initialSubMode = initialQuery.get('taggingSubMode') === 'mark-coverage' ? 'mark-coverage' : 'tag';
  const [mode, setMode] = useState(initialTagging ? 'tagging' : 'editing');
  const [taggingSubMode, setTaggingSubMode] = useState(initialTagging ? initialSubMode : 'tag');

  const setTaggingMode = useCallback(() => {
    setMode('tagging');
  }, []);

  const setEditingMode = useCallback(() => {
    setMode('editing');
  }, []);

  const isTaggingMode = mode === 'tagging';
  const isEditingMode = mode === 'editing';

  useEffect(() => {
    if (mode !== 'tagging') setTaggingSubMode('tag');
  }, [mode]);

  useEffect(() => {
    const params = new URLSearchParams(history.location.search);
    if (mode === 'tagging') {
      params.set('taggingMode', 'true');
      if (taggingSubMode === 'mark-coverage') params.set('taggingSubMode', 'mark-coverage');
      else params.delete('taggingSubMode');
    } else {
      params.delete('taggingMode');
      params.delete('taggingSubMode');
    }
    const next = params.toString();
    const current = history.location.search.replace(/^\?/, '');
    if (next === current) return;
    history.replace({
      pathname: history.location.pathname,
      search: next ? `?${next}` : '',
      hash: history.location.hash,
    });
  }, [mode, taggingSubMode, history]);

  return (
    <EditorModeContext.Provider
      value={{
        mode,
        setMode,
        setTaggingMode,
        setEditingMode,
        isTaggingMode,
        isEditingMode,
        taggingSubMode,
        setTaggingSubMode,
      }}
    >
      {children}
    </EditorModeContext.Provider>
  );
};

export const useEditorMode = () => {
  const context = useContext(EditorModeContext);
  if (!context) {
    return {
      mode: 'editing',
      setMode: () => {},
      setTaggingMode: () => {},
      setEditingMode: () => {},
      isTaggingMode: false,
      isEditingMode: true,
      taggingSubMode: 'tag',
      setTaggingSubMode: () => {},
    };
  }
  return context;
};

export const MAIN_EDITOR_ID = 'main-editor';
export const MultiEditorContext = React.createContext(MAIN_EDITOR_ID);

export const useEditorSelector = (selector, editorId) => {
  const contextEditorId = useContext(MultiEditorContext);
  return useSelector((state) => {
    const editor = state.document.editor[editorId ?? contextEditorId];
    return selector(editor);
  });
};
export const useEditorDispatch = () => {
  const dispatch = useDispatch();
  const editorId = useContext(MultiEditorContext);
  return useCallback(
    (action) => {
      action.editorId = editorId;
      return dispatch(action);
    },
    [editorId, dispatch]
  );
};
